import React, { useState, useMemo } from 'react';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import {
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ErrorBar,
    Scatter,
    Cell,
} from 'recharts';

// Paleta única do sistema
const C = {
    previsao: '#00008B',
    realizado: '#6495ED',
    media: '#FF0000',
    percentis: '#FF0000',
};

// Ícone X para marcadores pontuais
const CrossDot = (props) => {
    const { cx, cy, fill } = props;
    if (cx == null || cy == null) return null;
    const s = 6;
    return (
        <g>
            <line x1={cx - s} y1={cy - s} x2={cx + s} y2={cy + s} stroke={fill} strokeWidth={2} />
            <line x1={cx + s} y1={cy - s} x2={cx - s} y2={cy + s} stroke={fill} strokeWidth={2} />
            <line x1={cx} y1={cy - s - 3} x2={cx} y2={cy + s + 3} stroke={fill} strokeWidth={2} />
        </g>
    );
};

const CircleDot = (props) => {
    const { cx, cy, fill } = props;
    if (cx == null || cy == null) return null;
    return <circle cx={cx} cy={cy} r={5} fill={fill} stroke={fill} strokeWidth={1.5} />;
};

// Normaliza o data (split ou legacy) para array de objetos
const normalizeRows = (data) => {
    if (!data) return [];
    if (data.columns && Array.isArray(data.columns) && Array.isArray(data.data)) {
        return data.data.map((rowArr) => {
            const obj = {};
            data.columns.forEach((col, i) => { obj[col] = rowArr[i]; });
            return obj;
        });
    }
    const colNames = Object.keys(data);
    if (colNames.length === 0) return [];
    return Object.keys(data[colNames[0]]).map((idx) => {
        const obj = {};
        colNames.forEach((col) => { obj[col] = data[col][idx]; });
        return obj;
    });
};

const legendItem = (visible, setVisible, label) => (
    <div
        key={label}
        onClick={() => setVisible(!visible)}
        style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            cursor: 'pointer', userSelect: 'none',
            opacity: visible ? 1 : 0.5,
            color: visible ? 'black' : '#9ca3af',
            marginBottom: '2px',
        }}
    >
        {visible ? <HiEye /> : <HiEyeOff />}
        <span>{label}</span>
    </div>
);

const markerLegendItem = (marker, label, color) => (
    <div
        key={label}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#111827',
            marginTop: '4px',
        }}
    >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            {marker === 'circle' ? (
                <circle cx="8" cy="8" r="4" fill={color} stroke={color} strokeWidth="1.5" />
            ) : (
                <>
                    <line x1="4" y1="4" x2="12" y2="12" stroke={color} strokeWidth="2" />
                    <line x1="12" y1="4" x2="4" y2="12" stroke={color} strokeWidth="2" />
                    <line x1="8" y1="1" x2="8" y2="15" stroke={color} strokeWidth="2" />
                </>
            )}
        </svg>
        <span>{label}</span>
    </div>
);

const AnalysisChart = ({ data }) => {
    const [showPrevisao, setShowPrevisao] = useState(true);
    const [showRealizado, setShowRealizado] = useState(true);
    const [showMedia, setShowMedia] = useState(true);
    const [showPercentis, setShowPercentis] = useState(true);
    const [hiddenTransitions, setHiddenTransitions] = useState(new Set());

    const toggleTransition = (transicao) => {
        setHiddenTransitions(prev => {
            const next = new Set(prev);
            if (next.has(transicao)) next.delete(transicao);
            else next.add(transicao);
            return next;
        });
    };

    const { chartData, hasPrevisao, hasRealizado, hasMedia, hasPercentis } = useMemo(() => {
        const rows = normalizeRows(data).filter(r => r.transicao != null && r.transicao !== '');

        const hasP = rows.some(r => r["previsao (dias)"] != null);
        const hasR = rows.some(r => r["realizado (dias)"] != null);
        const hasM = rows.some(r => r["media (dias)"] != null);
        const hasPct = rows.some(r => r["p10 (dias)"] != null && r["p90 (dias)"] != null);

        const mapped = rows.map(r => {
            const p10 = r["p10 (dias)"] != null ? Number(r["p10 (dias)"]) : null;
            const p50 = r["p50 (dias)"] != null ? Number(r["p50 (dias)"]) : null;
            const p90 = r["p90 (dias)"] != null ? Number(r["p90 (dias)"]) : null;
            const errInf = (p10 != null && p50 != null) ? p50 - p10 : null;
            const errSup = (p50 != null && p90 != null) ? p90 - p50 : null;
            return {
                transicao: String(r.transicao),
                previsao: r["previsao (dias)"] != null ? Number(r["previsao (dias)"]) : null,
                realizado: r["realizado (dias)"] != null ? Number(r["realizado (dias)"]) : null,
                media: r["media (dias)"] != null ? Number(r["media (dias)"]) : null,
                p50,
                percentisErro: (errInf != null && errSup != null) ? [errInf, errSup] : null,
                p10, p90,
            };
        });

        return { chartData: mapped, hasPrevisao: hasP, hasRealizado: hasR, hasMedia: hasM, hasPercentis: hasPct };
    }, [data]);

    if (chartData.length === 0) return null;

    const displayData = chartData.map(d =>
        hiddenTransitions.has(d.transicao)
            ? { transicao: d.transicao, previsao: null, realizado: null, media: null, p50: null, percentisErro: null, p10: null, p90: null }
            : d
    );

    const allValues = displayData.flatMap(d => [
        showPrevisao && hasPrevisao ? d.previsao : null,
        showRealizado && hasRealizado ? d.realizado : null,
        showMedia && hasMedia ? d.media : null,
        showPercentis && hasPercentis ? d.p90 : null,
    ]).filter(v => v != null && !isNaN(v));
    const yMax = allValues.length > 0 ? Math.ceil(Math.max(...allValues) * 1.15) : 'auto';

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) return null;
        const d = displayData.find(r => r.transicao === label);
        return (
            <div style={{ backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: 6, padding: '10px 14px', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', minWidth: 160 }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: '#111' }}>{label}</div>
                {hasPrevisao && showPrevisao && d?.previsao != null && <div style={{ color: C.previsao }}>Previsão: <strong>{d.previsao.toFixed(0)} dias</strong></div>}
                {hasRealizado && showRealizado && d?.realizado != null && <div style={{ color: C.realizado }}>Realizado: <strong>{d.realizado.toFixed(0)} dias</strong></div>}
                {hasMedia && showMedia && d?.media != null && <div style={{ color: C.media }}>Média: <strong>{d.media.toFixed(1)} dias</strong></div>}
                {hasPercentis && showPercentis && d?.p10 != null && <div style={{ color: C.percentis }}>P10: {d.p10.toFixed(1)} | P50: {d.p50?.toFixed(1)} | P90: {d.p90?.toFixed(1)}</div>}
            </div>
        );
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mt-4">
            <div style={{ position: 'relative' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                <div style={{
                    position: 'absolute',
                    top: '30px',
                    right: '30px',
                    padding: '10px',
                    fontSize: '12px',
                    lineHeight: '1.6',
                    zIndex: 10,
                    color: '#000',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '4px',
                }}>
                    {hasPrevisao && legendItem(showPrevisao, setShowPrevisao, 'Previsão')}
                    {hasRealizado && legendItem(showRealizado, setShowRealizado, 'Realizado')}
                    {hasMedia && legendItem(showMedia, setShowMedia, 'Histórico (Média)')}
                    {hasPercentis && legendItem(showPercentis, setShowPercentis, 'Histórico (P10, P50, P90)')}
                    {(hasMedia || hasPercentis) && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(107, 114, 128, 0.25)' }}>
                            {hasMedia && markerLegendItem('circle', 'Média', C.media)}
                            {hasPercentis && markerLegendItem('cross', 'P50', C.percentis)}
                        </div>
                    )}
                </div>

                <ResponsiveContainer width="100%" height={500}>
                    <ComposedChart data={displayData} margin={{ top: 20, right: 20, left: 20, bottom: 70 }} barGap={2} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis
                            dataKey="transicao"
                            angle={-30}
                            textAnchor="end"
                            interval={0}
                            height={70}
                            label={{ value: 'Transição', position: 'insideBottom', offset: -10, fontSize: 13, fill: '#374151' }}
                            tick={(props) => {
                                const { x, y, payload } = props;
                                const hidden = hiddenTransitions.has(payload.value);
                                return (
                                    <g
                                        transform={`translate(${x},${y})`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => toggleTransition(payload.value)}
                                    >
                                        <title>{hidden ? 'Mostrar transição' : 'Esconder transição'}</title>
                                        <text
                                            x={0} y={0} dy={4}
                                            textAnchor="end"
                                            transform="rotate(-30)"
                                            fontSize={11}
                                            fill={hidden ? '#9ca3af' : '#374151'}
                                            textDecoration={hidden ? 'line-through' : 'none'}
                                        >
                                            {payload.value}
                                        </text>
                                    </g>
                                );
                            }}
                        />
                        <YAxis domain={[0, yMax]} tick={{ fontSize: 11, fill: '#374151' }} label={{ value: 'Dias', angle: -90, position: 'insideLeft', offset: 10, fontSize: 13, fill: '#374151' }} />
                        <Tooltip content={<CustomTooltip />} />

                        {hasPrevisao && showPrevisao && (
                            <Bar dataKey="previsao" legendType="none" fill={C.previsao} opacity={0.85} radius={[2, 2, 0, 0]} />
                        )}
                        {hasRealizado && showRealizado && (
                            <Bar dataKey="realizado" legendType="none" fill={C.realizado} opacity={0.85} radius={[2, 2, 0, 0]} />
                        )}
                        {hasMedia && showMedia && (
                            <Scatter dataKey="media" legendType="none" shape={<CircleDot fill={C.media} />} isAnimationActive={false}>
                                {chartData.map((_, i) => <Cell key={i} fill={C.media} />)}
                            </Scatter>
                        )}
                        {hasPercentis && showPercentis && (
                            <Scatter dataKey="p50" legendType="none" shape={<CrossDot fill={C.percentis} />} isAnimationActive={false}>
                                <ErrorBar dataKey="percentisErro" width={6} strokeWidth={2} stroke={C.percentis} direction="y" />
                                {chartData.map((_, i) => <Cell key={i} fill={C.percentis} />)}
                            </Scatter>
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default AnalysisChart;
